import { useEffect, useState } from "react";
import { Credential } from "@/types/jobPost";
import { User } from "@/types/user";
import { UpdateUserCredentialData } from "../mutations/updateUserCredential";

export const useUpdateCredentialManager = (user: User | null) => {
  const [userCredentials, setUserCredentials] = useState<
    User["user_credentials"]
  >([]);
  const [selectedCredentials, setSelectedCredentials] = useState<Credential[]>(
    [],
  );
  const [credentialDataObject, setCredentialDataObject] = useState<
    UpdateUserCredentialData[]
  >([]);

  useEffect(() => {
    if (user) {
      setUserCredentials(user.user_credentials || []);
      setSelectedCredentials(user.user_credentials.map(uc => uc.credential));
    }
  }, [user]);

  useEffect(() => {
    if (!user) return;

    const selectedIds = selectedCredentials.map(c => c.id);
    const originalIds = user.user_credentials.map(uc => uc.credential.id);

    const additions: UpdateUserCredentialData[] = selectedIds
      .filter(id => !originalIds.includes(id))
      .map(id => ({
        credential_id: id,
        is_student: false,
        is_currently_active: false,
        remove_credential: false,
      }));

    const removed = originalIds
      .filter(id => !selectedIds.includes(id))
      .map(id => ({
        credential_id: id,
        remove_credential: true,
      }));

    const retained = user.user_credentials
      .filter(uc => selectedIds.includes(uc.credential.id))
      .map(uc => ({
        credential_id: uc.credential.id,
        is_student: uc.is_student,
        is_currently_active: uc.is_currently_active,
        remove_credential: false,
      }));

    setCredentialDataObject([...retained, ...additions, ...removed]);
  }, [selectedCredentials, user]);

  const credentialsHaveChanged = () => {
    const original = userCredentials.map(uc => uc.credential.id).sort();
    const current = selectedCredentials.map(c => c.id).sort();
    return (
      original.length !== current.length ||
      original.some((id, i) => id !== current[i])
    );
  };

  const resetCredentials = () => {
    if (!user) return;
    setUserCredentials(user.user_credentials || []);
    setSelectedCredentials(user.user_credentials.map(uc => uc.credential));
    setCredentialDataObject([]);
  };

  return {
    userCredentials,
    selectedCredentials,
    setSelectedCredentials,
    credentialDataObject,
    credentialsHaveChanged,
    resetCredentials,
  };
};
